import { CommonModule } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  FileRejection,
  acceptedFormatsHint,
  fileRejection,
} from 'src/app/core/utils/file-acceptance';

/**
 * Marks the content an upload zone shows in place of its icon — an image preview, a chosen file.
 * Put it on the element handed to the dropzone: <img appFileDropzonePreview ...>.
 */
@Directive({
  selector: '[appFileDropzonePreview]',
  standalone: true,
})
export class FileDropzonePreviewDirective {}

/**
 * The single upload zone of the application: dashed frame, icon, "Drag & drop your file here", a
 * Browse button, and the accepted formats underneath.
 *
 * It exists because the same zone had been written five times — Cover AI, IBAN JM import, IBAN JM
 * OCR, Notice issuer, Upload snapshot — each with its own markup, its own stylesheet and its own
 * dragover/drop handlers, while a dozen other screens offered no drag and drop at all and hid an
 * input[type=file] behind a button. Divergence was not hypothetical: only some of those zones
 * validated what was dropped, and only some survived a file dropped next to them.
 *
 * Three behaviours belong here and must not be re-implemented by a host:
 *
 * 1. **Drop and pick are the same path.** Both end in handleFiles(), so a dropped file is validated
 *    exactly like a picked one — the accept attribute only ever filtered the picker.
 * 2. **A file lost outside the zone must not navigate the browser away.** The window-level guard
 *    neutralises the default action while a drop zone is on screen; without it, a near-miss drop
 *    opens the file in the tab and unloads the application, unsaved form included.
 * 3. **A refusal is said out loud**, in the zone itself and through filesRejected. A drop that
 *    silently does nothing reads as a broken page.
 *
 * The compact variant keeps every one of those behaviours and only tightens the layout to a single
 * row, for a file field sitting among others in a dense dialog.
 */
@Component({
  selector: 'app-file-dropzone',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule, TablerIconsModule],
  templateUrl: './file-dropzone.component.html',
  styleUrl: './file-dropzone.component.scss',
})
export class FileDropzoneComponent implements AfterContentChecked {
  /** 'panel' is the full zone; 'compact' is the single-row form-field version. */
  @Input() variant: 'panel' | 'compact' = 'panel';
  /** Sentence above the icon, saying what this particular zone is for (panel only). */
  @Input() description = '';
  @Input() title = 'Drag & drop your file here';
  @Input() browseLabel = 'Browse Files';
  /** HTML accept list — extensions, MIME types or wildcards, comma separated. */
  @Input() accept = '';
  /**
   * Accept list used to JUDGE a file, when it must be stricter than the picker filter above.
   * Some pickers have to be widened to stay usable — Windows Explorer hides .md unless its MIME
   * types are spelled out — and widening the picker must not widen what the zone accepts.
   * Empty: the accept list is used for both.
   */
  @Input() acceptValidation = '';
  /** Size ceiling in megabytes; null for no ceiling. */
  @Input() maxSizeMb: number | null = null;
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() icon = 'cloud-upload';
  /** Overrides the computed "Accepted: ... / Max size: ..." line when a zone needs its own wording. */
  @Input() hint = '';
  /** Accessible name of the zone, when the title alone would not say which upload it is. */
  @Input() ariaLabel = '';

  /** Files that passed validation. Never emitted empty. */
  @Output() filesSelected = new EventEmitter<File[]>();
  /** Files that were turned down, with the sentence shown to the user. Never emitted empty. */
  @Output() filesRejected = new EventEmitter<FileRejection[]>();

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ContentChild(FileDropzonePreviewDirective)
  preview?: FileDropzonePreviewDirective;

  dragOver = false;
  errorMessage = '';

  /** dragenter/dragleave also fire for every child element: count them to avoid flicker. */
  private dragDepth = 0;
  private hadPreview = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** The formats line, unless the host wrote its own. */
  get hintText(): string {
    return this.hint || acceptedFormatsHint(this.accept, this.maxSizeMb);
  }

  openFilePicker(): void {
    if (this.disabled) {
      return;
    }
    this.fileInput?.nativeElement?.click();
  }

  onBrowseClick(event: Event): void {
    // The whole zone opens the picker; without this the click would open it twice.
    event.stopPropagation();
    this.openFilePicker();
  }

  // Typed as Event, not KeyboardEvent: Angular hands a plain Event to a keydown.enter binding.
  onKeydown(event: Event): void {
    if (this.disabled) {
      return;
    }
    // Space scrolls the page by default — it must act on the zone it is aimed at.
    event.preventDefault();
    this.openFilePicker();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(input?.files);
    // Without this, picking the same file twice in a row raises no change event.
    if (input) {
      input.value = '';
    }
  }

  /**
   * A file dropped outside a drop zone must never trigger the browser default action (opening that
   * file), which would unload the whole application.
   */
  @HostListener('window:dragover', ['$event'])
  @HostListener('window:drop', ['$event'])
  preventWindowFileDrop(event: DragEvent): void {
    if (event.dataTransfer?.types?.includes('Files')) {
      event.preventDefault();
    }
  }

  onDragEnter(event: DragEvent): void {
    if (this.disabled || !event.dataTransfer?.types?.includes('Files')) {
      return;
    }
    event.preventDefault();
    this.dragDepth += 1;
    this.dragOver = true;
    this.cdr.markForCheck();
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled || !event.dataTransfer?.types?.includes('Files')) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(): void {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0 && this.dragOver) {
      this.dragOver = false;
      this.cdr.markForCheck();
    }
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOver = false;
    this.handleFiles(event.dataTransfer?.files);
  }

  /**
   * A projected preview can appear and disappear (an image bound to an *ngIf), and a content-query
   * result changing does not mark an OnPush view dirty on its own: without this the zone would keep
   * showing the icon next to a freshly chosen image, or the image after it was removed.
   */
  ngAfterContentChecked(): void {
    const hasPreview = !!this.preview;
    if (hasPreview !== this.hadPreview) {
      this.hadPreview = hasPreview;
      this.cdr.markForCheck();
    }
  }

  /** Clears the refusal message, for a host that reopens or resets its zone. */
  clearError(): void {
    if (this.errorMessage !== '') {
      this.errorMessage = '';
      this.cdr.markForCheck();
    }
  }

  /** The single entry point of both paths: validate, say what was refused, emit the rest. */
  private handleFiles(list: FileList | null | undefined): void {
    const files = Array.from(list ?? []);
    if (files.length === 0) {
      this.cdr.markForCheck();
      return;
    }

    if (!this.multiple && files.length > 1) {
      // Taking the first one silently would upload a file nobody pointed at.
      this.errorMessage = 'Only one file can be uploaded here.';
      this.cdr.markForCheck();
      return;
    }

    const accepted: File[] = [];
    const rejections: FileRejection[] = [];
    files.forEach((file) => {
      const rejection = fileRejection(file, {
        accept: this.acceptValidation || this.accept,
        maxSizeMb: this.maxSizeMb,
      });
      if (rejection) {
        rejections.push(rejection);
      } else {
        accepted.push(file);
      }
    });

    this.errorMessage = rejections.map((item) => item.message).join(' ');
    if (rejections.length > 0) {
      this.filesRejected.emit(rejections);
    }
    if (accepted.length > 0) {
      this.filesSelected.emit(accepted);
    }
    this.cdr.markForCheck();
  }
}
